import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, CalendarClock, Users } from 'lucide-react';
import { money, hrs, fmtDate, daysUntil, FLAG_META, STATUS_PILL, PRIORITY_PILL, barColor } from './fabDashUtils';
import { MilestoneTrack } from './MilestoneTrack';

const Bar = ({ pct }) => (
  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
    <div className={`h-full rounded-full ${barColor(pct)} transition-[width] duration-500`} style={{ width: `${Math.min(pct ?? 0, 100)}%` }} />
  </div>
);

const CostLine = ({ p, costVisible }) => {
  const c = p.cost;
  if (!costVisible) return <p className="text-xs text-slate-500">{hrs(c.labour_hours)} logged · {c.item_count} consumable/steel lines</p>;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <p className="text-sm font-bold text-slate-900">{money(c.total_cost)} <span className="text-xs font-normal text-slate-500">running cost</span></p>
        <p className="text-xs text-slate-500 truncate">
          {p.value ? <>of {money(p.value)} · <span className={`font-semibold ${p.budget_pct > 100 ? 'text-rose-600' : p.budget_pct > 80 ? 'text-amber-600' : 'text-emerald-700'}`}>{p.budget_pct}%</span></> : 'no job value on WO sheet'}
        </p>
      </div>
      <Bar pct={p.value ? p.budget_pct : null} />
    </div>
  );
};

const HoursLine = ({ p }) => {
  const est = p.estimated_hours || 0;
  const pct = est > 0 ? Math.round((p.actual_hours / est) * 100) : null;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <p className="text-sm font-bold text-slate-900">{hrs(p.actual_hours)} <span className="text-xs font-normal text-slate-500">logged</span></p>
        <p className="text-xs text-slate-500">{est > 0 ? <>of {hrs(est)} est · <span className={`font-semibold ${pct > 100 ? 'text-rose-600' : 'text-slate-700'}`}>{pct}%</span></> : 'no estimate'}</p>
      </div>
      <Bar pct={pct} />
    </div>
  );
};

const DueLine = ({ p }) => {
  const d = daysUntil(p.required_completion_date);
  const tone = d === null ? 'text-slate-500' : d < 0 ? 'text-rose-600' : d <= 7 ? 'text-amber-700' : 'text-slate-700';
  return (
    <div className="text-xs leading-5">
      <p className={`font-semibold ${tone} flex items-center gap-1`}><CalendarClock className="w-3.5 h-3.5" />
        {p.required_completion_date ? (d < 0 ? `Due ${fmtDate(p.required_completion_date)} · ${-d}d overdue` : d === 0 ? 'Due today' : `Due ${fmtDate(p.required_completion_date)} · ${d}d`) : 'No required date'}
      </p>
      {p.forecast_completion_date && p.status !== 'completed' && <p className="text-slate-500">Forecast {fmtDate(p.forecast_completion_date)}</p>}
      {!p.forecast_completion_date && p.planned_completion_date && <p className="text-slate-500">Planned finish {fmtDate(p.planned_completion_date)}</p>}
    </div>
  );
};

const Detail = ({ p, costVisible, fabRate }) => {
  const c = p.cost;
  const rows = [
    ['Labour', `${hrs(c.labour_hours)} (${hrs(c.labour_reg)} reg / ${hrs(c.labour_ot)} OT)`, c.labour_cost, c.labour_billable],
    ['Consumables', `${c.item_count} lines`, c.consumables_cost, c.consumables_sell],
    ['Stock steel', c.materials_weight ? `${c.materials_weight.toLocaleString()} lb` : '—', c.materials_cost, c.materials_sell],
  ];
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4" data-testid={`project-detail-${p.id}`}>
      {costVisible ? (
        <table className="text-sm w-full">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500">
            <th className="text-left font-semibold pb-1">Cost to date</th><th className="text-left font-semibold pb-1">Qty</th>
            <th className="text-right font-semibold pb-1">Actual cost</th><th className="text-right font-semibold pb-1">Billable</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(([l, q, cost, bill]) => (
              <tr key={l}><td className="py-1.5 text-slate-800">{l}</td><td className="py-1.5 text-slate-500 text-xs">{q}</td>
                <td className="py-1.5 text-right font-semibold text-slate-900">{money(cost, { cents: true })}</td><td className="py-1.5 text-right text-slate-600">{money(bill, { cents: true })}</td></tr>
            ))}
            <tr className="font-bold"><td className="pt-2 text-slate-900">Total</td><td />
              <td className="pt-2 text-right text-slate-900">{money(c.total_cost, { cents: true })}</td><td className="pt-2 text-right text-slate-700">{money(c.total_billable, { cents: true })}</td></tr>
            {p.value && <tr><td colSpan={2} className="pt-1 text-xs text-slate-500">Job value (WO sheet, pre-tax){p.po ? ` · ${p.po}` : ''}</td>
              <td className="pt-1 text-right text-xs font-semibold text-slate-700">{money(p.value, { cents: true })}</td>
              <td className={`pt-1 text-right text-xs font-semibold ${p.margin < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{p.margin < 0 ? '-' : '+'}{money(Math.abs(p.margin), { cents: true })} left</td></tr>}
          </tbody>
          <caption className="caption-bottom text-left text-[10px] text-slate-500 pt-1">
            Actual cost = pay rate + burden{p.rate_missing.length ? ` · default wage used for ${p.rate_missing.join(', ')}` : ''}. Billable = shop rate ${fabRate.regular}/${fabRate.overtime} OT + consumable & steel sell prices.
          </caption>
        </table>
      ) : <div />}
      <div className="text-xs space-y-1.5">
        {p.on_board && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-slate-600">
            {[['Received', p.date_received], ['Planned start', p.planned_start_date], ['Planned finish', p.planned_completion_date],
              ['Required', p.required_completion_date], ['Forecast', p.forecast_completion_date]].filter(([, v]) => v).map(([k, v]) => (
              <React.Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-800">{fmtDate(v)}</dd></React.Fragment>
            ))}
          </dl>
        )}
        <p className="text-slate-600 flex items-center gap-1 pt-1"><Users className="w-3.5 h-3.5" /> {p.crew.length ? p.crew.join(', ') : 'No hours logged yet'}</p>
        {p.first_worked_date && <p className="text-slate-500">Worked {fmtDate(p.first_worked_date)} → {fmtDate(p.last_worked_date)} · {hrs(p.hours_last_30d)} in last 30 days</p>}
        {p.project_manager && <p className="text-slate-500">PM {p.project_manager}{p.work_area ? ` · ${p.work_area}` : ''}</p>}
        {p.warnings.map((w) => <p key={w} className="text-amber-700">⚠ {w}</p>)}
        {p.notes && <p className="text-slate-600 italic">"{p.notes}"</p>}
        {p.on_board && <a href="/fab-scheduler" className="inline-flex items-center gap-1 text-blue-700 hover:underline font-medium pt-1" data-testid={`open-scheduler-${p.id}`}><ExternalLink className="w-3 h-3" /> Open in Fab Scheduler</a>}
      </div>
    </div>
  );
};

export const ProjectRow = ({ p, costVisible, fabRate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`px-4 py-3 ${p.working_now ? 'bg-emerald-50/40' : ''}`} data-testid={`project-row-${p.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left" data-testid={`project-toggle-${p.id}`}>
        <div className="flex items-start gap-2 flex-wrap">
          <span className="font-mono text-blue-700 font-bold text-sm">{p.work_order_number}</span>
          <span className="text-sm font-semibold text-slate-900 min-w-0 truncate max-w-full">{p.customer_name}{p.project_name ? <span className="font-normal text-slate-600"> — {p.project_name}</span> : null}</span>
          <span className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
            {PRIORITY_PILL[p.priority] && <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${PRIORITY_PILL[p.priority]}`}>{p.priority}</span>}
            <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_PILL[p.status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>{p.status_label}</span>
            {p.flags.filter((f) => f !== 'rate_missing' && f !== 'waiting').map((f) => (
              <span key={f} className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${FLAG_META[f].cls}`}>{FLAG_META[f].label}</span>
            ))}
            {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </span>
        </div>
        <div className="mt-2.5 hidden md:block"><MilestoneTrack p={p} /></div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.3fr_1fr_1fr] gap-x-6 gap-y-2 items-end">
          <CostLine p={p} costVisible={costVisible} />
          <HoursLine p={p} />
          <DueLine p={p} />
        </div>
      </button>
      {open && <Detail p={p} costVisible={costVisible} fabRate={fabRate} />}
    </div>
  );
};
